import { create } from 'zustand';
import { ROUTES } from "@/lib/constants";

export interface TaxonomyTerm {
  id: string;
  key: string;
  label: string;
  description?: string;
  iconKey?: string;
  translated: boolean;
  sortOrder: number;
}

export interface TaxonomyGroup {
  groupKey: string;
  terms: TaxonomyTerm[];
}

interface TaxonomyState {
  groups: Record<string, TaxonomyTerm[]>;
  isLoading: boolean;
  error: string | null;
  pendingPromises: Record<string, Promise<void>>;
  fetchGroups: (groupKeys: string[], locale?: string) => Promise<void>;
  createTerm: (groupKey: string, data: Partial<TaxonomyTerm>, locale?: string) => Promise<TaxonomyTerm>;
}

export const useTaxonomyStore = create<TaxonomyState>((set, get) => ({
  groups: {},
  isLoading: false,
  error: null,
  pendingPromises: {},
  
  fetchGroups: async (groupKeys: string[], locale: string = 'en') => {
    const fetchLocale = locale;
    const state = get();
    
    // Create locale-specific keys for caching (e.g. "en:bed_types")
    const getCacheKey = (key: string) => `${locale}:${key}`;
    
    // Find keys that are neither loaded nor currently being fetched
    const missingGroups = groupKeys.filter(
      key => !state.groups[getCacheKey(key)] && !state.pendingPromises[getCacheKey(key)]
    );
    
    // If all requested keys are already loaded or being fetched, wait for any pending ones
    if (missingGroups.length === 0) {
      const promisesToWait = groupKeys
        .map(key => state.pendingPromises[getCacheKey(key)])
        .filter(Boolean);
      if (promisesToWait.length > 0) {
        await Promise.all(promisesToWait);
      }
      return;
    }
    
    set({ isLoading: true, error: null });
    
    // Create the promise for fetching the missing groups
    const groupsParam = missingGroups.join(',');
    // The API's own docs: "Omit for English" — it treats an explicit `en` as
    // an unrecognized locale code and 400s, rather than as "the default".
    const localeQuery = fetchLocale && fetchLocale !== 'en' ? `&locale=${fetchLocale}` : '';
    const fetchPromise = (async () => {
      try {
        const res = await fetch(`${ROUTES.TAXONOMY.TERMS}?groups=${groupsParam}${localeQuery}`);
        
        if (!res.ok) {
          throw new Error('Failed to fetch taxonomy terms');
        }
        
        const data: TaxonomyGroup[] = await res.json();
        
        const newGroupsRecord: Record<string, TaxonomyTerm[]> = {};
        data.forEach(group => {
          // De-dupe by id — lists render keyed by term.id, so a duplicate here means a duplicate React key.
          const seen = new Set<string>();
          const deduped = group.terms.filter(term => {
            if (seen.has(term.id)) return false;
            seen.add(term.id);
            return true;
          });
          newGroupsRecord[getCacheKey(group.groupKey)] = deduped.sort((a, b) => a.sortOrder - b.sortOrder);
        });
        
        set(prev => {
          const nextPending = { ...prev.pendingPromises };
          missingGroups.forEach(k => delete nextPending[getCacheKey(k)]);
          return {
            groups: { ...prev.groups, ...newGroupsRecord },
            pendingPromises: nextPending,
            isLoading: Object.keys(nextPending).length > 0
          };
        });
        
      } catch (err: any) {
        set(prev => {
          const nextPending = { ...prev.pendingPromises };
          missingGroups.forEach(k => delete nextPending[getCacheKey(k)]);
          return { 
            error: err.message, 
            pendingPromises: nextPending,
            isLoading: Object.keys(nextPending).length > 0 
          };
        });
      }
    })();

    // Store this promise for all missing keys so concurrent calls can wait on it
    set(prev => {
      const nextPending = { ...prev.pendingPromises };
      missingGroups.forEach(k => {
        nextPending[getCacheKey(k)] = fetchPromise;
      });
      return { pendingPromises: nextPending };
    });

    await fetchPromise;
  },

  createTerm: async (groupKey: string, data: Partial<TaxonomyTerm>, locale: string = 'en') => {
    const res = await fetch('/api/taxonomy/terms', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...data, groupKey })
    });
    
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.message || 'Failed to create taxonomy term');
    }
    
    const newTerm: TaxonomyTerm = await res.json();
    
    // Add it to the local cache immediately so the UI updates
    set(prev => {
      const cacheKey = `${locale}:${groupKey}`;
      const currentGroup = prev.groups[cacheKey] || [];
      return {
        groups: {
          ...prev.groups,
          [cacheKey]: [...currentGroup, newTerm]
        }
      };
    });
    
    return newTerm;
  }
}));
